import type { BookLanguage, BookSummary } from "@contract";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { StateChip } from "../../components/control/StateChip";
import { StatLine } from "../../components/data/StatLine";
import { InlineSpinner } from "../../components/feedback/InlineSpinner";
import { dashboardAction, dashboardActionPath, isWorkingState } from "./action";

/** "Deutsch" or "Englisch" for a Book's own language; two static keys, not a lookup. */
function BookLanguageName({ language }: { language: BookLanguage }) {
  const { t } = useTranslation("common");
  return <>{language === "de" ? t("bookLanguage.de") : t("bookLanguage.en")}</>;
}

/**
 * One row of the book list from docs/mockup/Upload.dc.html: cover slot, serif
 * title, a mono line of facts, and the one action that state deserves. A
 * Failed Book is tinted and its action leads to the Book page, where the
 * retry sits next to the job it retries.
 */
export function BookCard({ book }: { book: BookSummary }) {
  const { t } = useTranslation(["dashboard", "common"]);
  const action = dashboardAction(book.state);
  const failed = book.state === "failed";
  const working = isWorkingState(book.state);

  // Built outside the JSX so each fact keeps its own separator when the line
  // wraps: the German action label is long enough to push the last fact onto a
  // second line, and a dot that stays behind reads as a missing word.
  const facts: ReactNode[] = [];
  if (book.language) facts.push(<BookLanguageName language={book.language} />);
  facts.push(t("common:chapterCount", { count: book.chapterCount }));
  facts.push(
    <>
      <StateChip kind="book" state={book.state} />
      {working ? <InlineSpinner size={11} className="ml-1.5 text-ink-3" /> : null}
    </>,
  );
  if (book.renderedAt) {
    facts.push(t("dashboard:list.renderedOn", { date: book.renderedAt }));
  } else if (book.approvedCount > 0) {
    facts.push(
      t("dashboard:list.approvedOf", {
        approved: book.approvedCount,
        total: book.chapterCount,
      }),
    );
  }

  return (
    <article
      data-component="BookCard"
      data-state={book.state}
      className={`flex items-center gap-4 rounded-md border px-5 py-4 ${
        failed ? "border-danger-line bg-danger-soft" : "border-line bg-surface"
      }`}
    >
      <div aria-hidden="true" className="h-[58px] w-10 shrink-0 rounded-sm bg-line" />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <Link
          to={`/books/${book.id}/overview`}
          className="truncate font-serif text-title-sm font-semibold text-ink hover:text-accent"
        >
          {book.title ?? t("dashboard:list.untitled")}
        </Link>
        <StatLine items={facts} />
      </div>
      <Link
        to={dashboardActionPath(book)}
        className={`shrink-0 whitespace-nowrap ${failed ? "text-danger" : "text-accent"} hover:underline`}
      >
        {t(`dashboard:action.${action}`)}
      </Link>
    </article>
  );
}
